import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { TimeOffApprovalFlow } from '@app/models/time-off-v3/time-off-approval-flow.model';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { AnalyticService, NotifyService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { TimeOffType } from '@models/time-off-v3/time-off-type.model';
import { getTime, setTime } from '@time-off-v3/functions/time-unit-converter';
import { AccrualFrequencies, DECIMAL_PLACES } from '@time-off-v3/meta/time-off-meta';
import { TimeOffCreateFlowService } from '@time-off-v3/services';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';

@Component({
    templateUrl: './basic-information-update.template.html',
    styleUrls: ['./basic-information-update.style.scss'],
})
export class BasicInformationUpdateView extends BaseForm implements OnInit, OnDestroy {
    isLoading = false;
    timeOffPolicy: TimeOffPolicy = new TimeOffPolicy();
    timeOffType = new TimeOffType();
    timeOffApprovalFlows: TimeOffApprovalFlow[] = [];
    timeOffTypes: TimeOffType[] = [];

    private timeOffTypesSubscription!: Subscription;
    private timeOffApprovalFlowsSubscription!: Subscription;

    private timeOffPolicyClone: TimeOffPolicy;

    constructor(
        private analyticService: AnalyticService,
        private timeOffCreateFlowService: TimeOffCreateFlowService,
        private notify: NotifyService,
        private route: ActivatedRoute,
        private location: Location,
        private router: Router
    ) {
        super();

        this.timeOffPolicy = this.route.snapshot.data.models.get('policy');
    }

    async ngOnInit(): Promise<void> {
        /**
         * These two subscriptions are here because we're loading time off types and approval flows
         * through the timeOffCreateFlowService. The service will publish to these subscribers once
         * the data is resolved.
         */
        this.timeOffTypesSubscription = this.timeOffCreateFlowService.timeOffTypes$.subscribe((types) => {
            this.timeOffTypes = types;
        });

        this.timeOffApprovalFlowsSubscription = this.timeOffCreateFlowService.timeOffApprovalFlows$.subscribe(
            (approvalFlows) => {
                this.timeOffApprovalFlows = approvalFlows;
            }
        );

        this.timeOffPolicyClone = cloneDeep(this.timeOffPolicy);

        this.isLoading = true;

        try {
            await Promise.all([
                this.timeOffCreateFlowService.loadAllTimeOffTypes(),
                this.timeOffCreateFlowService.loadAllTimeOffApprovals(),
            ]);
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    ngOnDestroy(): void {
        this.timeOffTypesSubscription.unsubscribe();
        this.timeOffApprovalFlowsSubscription.unsubscribe();
    }

    onCancel(): void {
        this.location.back();
    }

    async onSave(): Promise<void> {
        this.isLoading = true;

        try {
            this.recalculateTimeOffPolicy();

            if (this.timeOffPolicy.timeOffRequestRule.isDirty()) {
                await this.timeOffPolicy.timeOffRequestRule.save();
            }

            await this.timeOffPolicy.save();

            this.analyticService.trackEvent(AnalyticEvents.TimeOffTimeOffPolicyEditTimeOffPolicy);
            this.notify.success('time-off-v3.update.savedSuccessfully');
            this.router.navigate(['/', 'time-off', 'v3', 'types-and-policies']);
        } catch (error) {
            this.notify.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    redirectToTimeOffCreation(createSelected: boolean): void {
        if (!createSelected) {
            return;
        }

        this.router.navigate(['/', 'time-off', 'v3', 'types-and-policies', 'types', 'create']);
    }

    /**
     * The purpose of this function is to make sure that when we change the time off type the policy has the
     * proper values saved based on the displayInHours property being true or false.
     */
    private recalculateTimeOffPolicy(): void {
        /**
         * If the displayInHours is both false that means it's days from the previous time off type and the current time off type
         *  Meaning we don't rerun calculations.
         */
        if (!this.timeOffPolicy.timeOffType.displayInHours && !this.timeOffPolicyClone.timeOffType.displayInHours) {
            return;
        }

        if (
            this.timeOffPolicy.timeOffType.displayInHours === this.timeOffPolicyClone.timeOffType.displayInHours &&
            this.timeOffPolicy.timeOffType.hoursPerDay === this.timeOffPolicyClone.timeOffType.hoursPerDay
        ) {
            return;
        }

        const oldDaysPerPeriod = getTime(
            this.timeOffPolicyClone.daysAccruedPerPeriod * AccrualFrequencies[this.timeOffPolicyClone.accrualFrequency],
            this.timeOffPolicyClone.timeOffType
        ) as number;

        this.timeOffPolicy.daysAccruedPerPeriod = setTime(
            +oldDaysPerPeriod.toFixed(DECIMAL_PLACES) / AccrualFrequencies[this.timeOffPolicy.accrualFrequency],
            this.timeOffPolicy?.timeOffType
        ) as number;

        this.timeOffPolicy.balanceCap = this.timeOffPolicyClone.balanceCap;
        this.timeOffPolicy.carryoverCap = this.timeOffPolicyClone.carryoverCap;

        // update time off type so getTime/setTime calculations run correctly
        this.timeOffPolicy.timeOffRequestRule.timeOffPolicy.timeOffType.displayInHours =
            this.timeOffPolicy.timeOffType.displayInHours;
        this.timeOffPolicy.timeOffRequestRule.timeOffPolicy.timeOffType.hoursPerDay =
            this.timeOffPolicy.timeOffType.hoursPerDay;

        this.timeOffPolicy.timeOffRequestRule.timeOffType = this.timeOffPolicy.timeOffType;

        this.timeOffPolicy.timeOffRequestRule.negativeBalanceCap = this.timeOffPolicyClone.timeOffRequestRule
            .negativeBalanceCap as number;
        this.timeOffPolicy.timeOffRequestRule.minDayAmount = this.timeOffPolicyClone.timeOffRequestRule
            .minDayAmount as number;
        this.timeOffPolicy.timeOffRequestRule.maxLength = this.timeOffPolicyClone.timeOffRequestRule
            .maxLength as number;
    }
}
